// Profile sync and the offline queue (docs/SPEC.md "Profile sync"). Port of web/profile.js's syncProfile / profileSet.
// Each change is stamped (ms) and the newest change to an item wins on the server; changes wait in localStorage while
// the server can't be reached, and go up in one batch (POST /profile/changes) when it can. With nothing queued a sync
// is a plain GET /profile. The network, storage, clock and retry timer are passed in, so tests run without waiting.
import { emptyData, itemId, makeChanges, type Item, type Kind, type ProfileData, type RawEvent } from "./model";

/** Retry delay while the server can't be reached. */
export const RETRY_MS = 30_000;
/** The most changes one request carries (backend/nowish/api.py ProfileChanges). */
export const BATCH_MAX = 2000;

/** The server read the request and said no (a bad change). Busy (429) and timed out (408) are not that: those wait and retry. */
const refused = (r: Response) => r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 429;

type Timer = unknown;
export interface SyncDeps {
  fetch: typeof fetch;
  /** localStorage, or null where it's refused. */
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  /** `?profile=<id>`: another profile (tests); null = the default one. */
  profileId: string | null;
  now: () => number;
  setTimer: (fn: () => void, ms: number) => Timer;
  clearTimer: (t: Timer) => void;
  /** Something the page shows changed (a tap, or a sync that brought something new). */
  onChange: () => void;
}

/** An event as `set` receives it: the page's parsed event (Dates) or the server's copy (text). */
export type EventCopy = { id: string; start: Date | string; end?: Date | string | null } & Record<string, unknown>;

const iso = (d: Date | string | null | undefined) => (d instanceof Date ? d.toISOString() : d);

export class ProfileSync {
  data: ProfileData = emptyData();
  /** The server has answered at least once this session. */
  loaded = false;
  /** Changes the server refused and this device gave up on this session (they are shown as never made). */
  rejected: Item[] = [];
  private readonly qs: string;
  private readonly storageKey: string;
  private syncing: { again: boolean } | null = null;
  private retry: Timer = null;

  constructor(private readonly deps: SyncDeps) {
    const id = deps.profileId;
    this.qs = id ? `?profile=${encodeURIComponent(id)}` : "";
    // Not the old site's key (nowish-profile): the two sites share an origin and shouldn't write each other's queue.
    this.storageKey = `nowish-next-profile${id ? "-" + id : ""}`;
    try {
      const saved = JSON.parse(deps.storage?.getItem(this.storageKey) ?? "null");
      if (saved && typeof saved === "object") {
        this.data = { items: saved.items || {}, outbox: saved.outbox || [], events: saved.events || {} };
      }
    } catch { /* damaged: start empty */ }
  }

  /** One change from this device (plus the consistency rules the server applies). Shows at once; syncs in the background. */
  set(kind: Kind, key: string, on: boolean, opts: { event?: EventCopy; value?: string | null } = {}): void {
    const changes = makeChanges(this.data, kind, key, on, opts.value ?? null, this.deps.now());
    if (!changes.length) return;
    const e = opts.event;
    if (e && on) {
      this.data = { ...this.data, events: { ...this.data.events, [changes[0]!.key]: { ...e, start: iso(e.start), end: iso(e.end) } as RawEvent } };
    }
    this.data = { ...this.data, outbox: [...this.data.outbox, ...changes] };
    this.save();
    this.deps.onChange();
    void this.sync();
  }

  /** Sends the queued changes (or none) and takes the server's profile. Retries in 30 s while offline. */
  async sync(): Promise<void> {
    if (this.syncing) { this.syncing.again = true; return; }
    const run: { again: boolean } = (this.syncing = { again: false });
    this.deps.clearTimer(this.retry);
    try {
      do {
        run.again = false;
        const sending = this.data.outbox.slice(0, BATCH_MAX);
        let res = sending.length ? await this.post(sending) : await this.deps.fetch(`/profile${this.qs}`);
        if (!res.ok && sending.length && refused(res)) {
          // One bad change refuses its whole batch. Find which, one at a time, and give those up: they would be
          // refused every time, and nothing behind them could ever sync.
          for (const c of sending) {
            const one = sending.length === 1 ? res : await this.post([c]);
            if (one.ok) continue;
            if (!refused(one)) throw new Error(`profile ${one.status}`);
            this.drop(c);
          }
          res = await this.deps.fetch(`/profile${this.qs}`);
        }
        if (!res.ok) throw new Error(`profile ${res.status}`);
        const p = (await res.json()) as { items: Item[]; events?: Record<string, RawEvent> };
        const items = Object.fromEntries(p.items.map((it) => [itemId(it), it]));
        const outbox = this.data.outbox.filter((c) => !sending.includes(c));
        // The server's copies of starred / hidden events. One you starred while this request was out isn't in its
        // answer yet: keep the copy made here until the server sends its own.
        const events: Record<string, RawEvent> = { ...(p.events || {}) };
        for (const c of outbox) {
          const mine = this.data.events[c.key];
          if ((c.kind === "stars" || c.kind === "hidden-events") && c.on && !events[c.key] && mine) events[c.key] = mine;
        }
        // Most syncs (every minute) bring nothing new: then nothing is redrawn.
        const same = !sending.length && this.loaded && JSON.stringify(items) === JSON.stringify(this.data.items)
          && JSON.stringify(events) === JSON.stringify(this.data.events);
        this.data = { items, events, outbox };
        this.loaded = true;
        if (outbox.length) run.again = true; // more than one request's worth, or taps made meanwhile
        if (same) continue;
        this.save();
        this.deps.onChange();
      } while (run.again);
    } catch (err) {
      console.warn("profile sync failed", err);
      this.retry = this.deps.setTimer(() => { void this.sync(); }, RETRY_MS);
    } finally {
      this.syncing = null;
    }
  }

  /**
   * Sends changes, the latest per item only: the newest change to an item wins anyway, and a device clock ahead of the
   * server's is pulled back to the server's time, so two changes to one item in a batch would tie and the later lose.
   */
  private post(changes: Item[]) {
    const latest = changes.filter((c, i) => !changes.some((d, j) => j > i && itemId(d) === itemId(c)));
    return this.deps.fetch(`/profile/changes${this.qs}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ changes: latest }),
    });
  }

  /** Gives up on a change the server refused: it comes off the queue, the view shows it as never made. */
  private drop(c: Item) {
    console.warn("profile: the server refused a change; dropped", c);
    this.rejected = [...this.rejected, c];
    this.data = { ...this.data, outbox: this.data.outbox.filter((x) => x !== c) };
    this.save();
    this.deps.onChange();
  }

  /**
   * Another tab saved its queue (the `storage` event): takes in the changes it queued that this tab hasn't seen and
   * the server hasn't acknowledged, so neither tab's save wipes the other's. Only ever adds; each tab sends its own.
   */
  adoptStored(): void {
    let stored: Partial<ProfileData> | null = null;
    try { stored = JSON.parse(this.deps.storage?.getItem(this.storageKey) ?? "null"); } catch { return; }
    if (!stored || typeof stored !== "object") return;
    const have = new Set(this.data.outbox.map((c) => `${itemId(c)}|${c.at}`));
    const fresh = (stored.outbox ?? []).filter((c) => !have.has(`${itemId(c)}|${c.at}`) && (this.data.items[itemId(c)]?.at ?? -1) < c.at);
    const events = Object.fromEntries(Object.entries(stored.events ?? {}).filter(([id]) => !this.data.events[id]));
    if (!fresh.length && !Object.keys(events).length) return;
    this.data = { ...this.data, outbox: [...this.data.outbox, ...fresh].sort((a, b) => a.at - b.at), events: { ...this.data.events, ...events } };
    this.deps.onChange();
    if (fresh.length) void this.sync();
  }

  private save() {
    try { this.deps.storage?.setItem(this.storageKey, JSON.stringify(this.data)); } catch { /* storage full or refused */ }
  }
}
